// ─── Looker's hidden columns, honoured in one place ──────────────────────────
// "Hidden" in Looker is a VISUALIZATION setting, not a query one: a hidden
// column is still selected, still calculated (a table calc usually depends on
// it) and still comes back in the result — the vis simply doesn't draw it. It
// lives in the tile's vis config as `hidden_fields` (whole columns) and
// `hidden_pivots` (a measure hidden under ONE pivot column only).
//
// Every tile renderer, the tile editor's results grid and anything that ships a
// tile's columns somewhere else (an AI insight, an export) asks this module what
// to draw, so one imported tile can never show two different column sets.
//
// BROWSER MIRROR of server/visFields.js — keep the two in sync.

export function hiddenFieldSet(vis) {
  return new Set(Array.isArray(vis?.hidden_fields) ? vis.hidden_fields : []);
}

export function isHiddenField(vis, name) {
  return hiddenFieldSet(vis).has(name);
}

// A result's `fields` block with every hidden column stripped out — same shape
// in as out, so callers keep walking it exactly as they walked Looker's own.
export function visibleFields(fields, vis) {
  const hidden = hiddenFieldSet(vis);
  if (!fields || !hidden.size) return fields;
  const keep = (arr) => (Array.isArray(arr) ? arr.filter((f) => !hidden.has(f?.name)) : arr);
  return {
    ...fields,
    dimensions: keep(fields.dimensions),
    measures: keep(fields.measures),
    table_calculations: keep(fields.table_calculations),
  };
}

// What a tile actually draws, split the way every renderer here wants it:
// visible dimensions, and visible measures WITH the table calculations folded in
// (Looker treats a calc as just another measure column).
export function visibleColumns(fields, vis) {
  const hidden = hiddenFieldSet(vis);
  const keep = (arr) => (arr || []).filter((f) => !hidden.has(f?.name));
  return {
    dimensions: keep(fields?.dimensions),
    measures: [...keep(fields?.measures), ...keep(fields?.table_calculations)],
  };
}

// The measures to draw under ONE pivot column: Looker can hide a measure for a
// single pivot value (e.g. a "% change" calc shown only under the current
// event), which is `hidden_pivots: { <pivotKey>: { measure_names: [...] } }`.
// `is_entire_pivot_hidden: true` on that same entry hides the WHOLE pivot
// column group (every measure under, say, "KFF25") — nothing is drawn for it.
export function visibleForPivot(measures, vis, pivotKey) {
  const entry = (vis?.hidden_pivots || {})[pivotKey];
  if (entry?.is_entire_pivot_hidden === true) return [];
  const hide = entry?.measure_names || [];
  if (!hide.length) return measures || [];
  return (measures || []).filter((m) => !hide.includes(m?.name));
}

// ─── Hiding ONE column of a pivoted table (browser-only writer) ──────────────
// In a pivoted table one field becomes a column under EVERY pivot value
// ("Tickets" under KFF27 and again under KFF26). Hiding the field hides all of
// them; Looker can also hide just one — the "% change" calc under the oldest
// event, where it has nothing to compare against. That is `hidden_pivots`,
// which the renderers above already honour; these two let the tile editor read
// and write it, so a single column can be hidden without a trip back to Looker.
export function isHiddenForPivot(vis, pivotKey, name) {
  const hide = (vis?.hidden_pivots || {})[pivotKey]?.measure_names || [];
  return hide.includes(name);
}

// The vis's next `hidden_pivots` with `name` hidden (or shown) under one pivot.
// Every other entry Looker saved is kept; an emptied pivot entry is dropped.
export function setHiddenForPivot(vis, pivotKey, name, hide) {
  const all = { ...(vis?.hidden_pivots || {}) };
  const cur = Array.isArray(all[pivotKey]?.measure_names) ? all[pivotKey].measure_names : [];
  const next = hide ? [...new Set([...cur, name])] : cur.filter((m) => m !== name);
  return tidyPivotEntry(all, pivotKey, { ...(all[pivotKey] || {}), measure_names: next });
}

// The WHOLE pivot column group ("hide every KFF25 column"), Looker's
// `is_entire_pivot_hidden`. The per-column choices underneath are kept, so
// showing the group again brings back exactly what was shown before.
export function isPivotHidden(vis, pivotKey) {
  return (vis?.hidden_pivots || {})[pivotKey]?.is_entire_pivot_hidden === true;
}

export function setPivotHidden(vis, pivotKey, hide) {
  const all = { ...(vis?.hidden_pivots || {}) };
  const entry = { ...(all[pivotKey] || {}) };
  if (hide) entry.is_entire_pivot_hidden = true;
  else delete entry.is_entire_pivot_hidden;
  return tidyPivotEntry(all, pivotKey, entry);
}

// Drop an entry that no longer hides anything, so the vis doesn't fill up with
// `{ measure_names: [] }` left-overs.
function tidyPivotEntry(all, pivotKey, entry) {
  const names = Array.isArray(entry.measure_names) ? entry.measure_names : [];
  if (!names.length) delete entry.measure_names;
  if (Object.keys(entry).length) all[pivotKey] = entry;
  else delete all[pivotKey];
  return all;
}
